// assets/scroll-sequence.js

class ScrollSequence {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    
    // 1. 解析 Liquid 传过来的配置 JSON
    const configScript = document.getElementById(`Config-${this.sectionId}`);
    if (!configScript) return;
    
    try {
      this.config = JSON.parse(configScript.textContent);
    } catch (e) {
      console.error("[ScrollSequence] JSON 解析失败，请检查配置", e);
      return;
    }

    this.canvas = section.querySelector('canvas');
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');
    
    this.totalFrames = this.config.endFrame - this.config.startFrame + 1;
    this.images = new Array(this.totalFrames);
    this.currentFrame = -1;
    this.hasStartedLoading = false;
    this.fadeRange = 10; // 文字渐变缓冲帧数

    // 2. 初始化核心流程
    this.initObserver();
    this.setupResize();
  }

  pad(n) {
    const length = this.config.paddingLength || 5; // 动态读取位数，默认 5 位
    return String(n).padStart(length, '0');
  }

  getUrl(index) {
    const frameNum = this.config.startFrame + index;
    return `${this.config.baseUrl}${this.config.prefix}${this.pad(frameNum)}${this.config.ext}?v=1783506400`;
  }

  // IntersectionObserver 懒加载触发
  initObserver() {
    // 如果关闭了预加载，或者处于 Shopify 后台定制模式下，直接强制加载
    if (!this.config.enablePreload || window.Shopify?.designMode) {
      this.startLoading();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.hasStartedLoading) {
        this.hasStartedLoading = true;
        this.startLoading();
        observer.disconnect(); // 触发后即断开
      }
    }, {
      rootMargin: `${this.config.preloadDistance || 500}px 0px`, // 提前 N px 触发
      threshold: 0
    });

    observer.observe(this.section);
  }

  // 图片分批加载策略 (Apple 思路)
  startLoading() {
    const initialCount = Math.min(this.config.initialFrames || 20, this.totalFrames);
    
    // 首批优先加载核心首屏帧
    for (let i = 0; i < initialCount; i++) {
      this.loadImage(i, i === 0);
    }

    // 后台空闲时加载剩余帧
    if (this.totalFrames > initialCount) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => this.loadRemainingImages(initialCount));
      } else {
        setTimeout(() => this.loadRemainingImages(initialCount), 200);
      }
    }
  }

  loadRemainingImages(startIndex) {
    let i = startIndex;
    const loadNextBatch = () => {
      // 每次空闲加载 8 张，避免占用主线程
      const batchEnd = Math.min(i + 8, this.totalFrames);
      while (i < batchEnd) {
        this.loadImage(i);
        i++;
      }
      if (i < this.totalFrames) {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(loadNextBatch);
        } else {
          setTimeout(loadNextBatch, 50);
        }
      }
    };
    loadNextBatch();
  }

  loadImage(index, isFirst = false) {
    const img = new Image();
    img.onload = () => {
      if (isFirst) {
        this.setupCanvas(img);
        this.drawCanvas(0); // 确保第一帧画出
        this.initScrollEvent();
      }
      // 容错：如果滚动太快，图片刚下载完正好处在这一帧，立刻重绘
      if (this.currentFrame === index) {
        this.drawCanvas(index);
      }
    };
    img.onerror = () => {
      console.warn(`[ScrollSequence] Failed to load frame ${index}: ${this.getUrl(index)}`);
    };
    img.src = this.getUrl(index);
    this.images[index] = img;
  }

  // Retina 优化 Canvas 初始化
  setupCanvas(img) {
    this.canvas.width = img.width;   
    this.canvas.height = img.height; 
  }

  setupResize() {
    window.addEventListener('resize', () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        if (this.images[0] && this.images[0].complete) {
          this.drawCanvas(this.currentFrame > -1 ? this.currentFrame : 0);
        }
      }, 150);
    }, { passive: true });
  }

  drawCanvas(frameIndex) {
    const img = this.images[frameIndex];
    if (img && img.complete) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);
    }
  }

  /* ==========================================
     🔧 修复后的丝滑 Lerp 滚动监听逻辑
     ========================================== */
  initScrollEvent() {
    this.targetFrame = 0;      // 目标帧
    this.interpolatedFrame = 0; // 缓动当前帧
    this._isAnimating = true;
    
    this._handleScroll = () => {
      const rect = this.section.getBoundingClientRect();
      let progress = -rect.top / (rect.height - window.innerHeight);
      progress = Math.max(0, Math.min(1, progress));
      const newTarget = Math.min(Math.floor(progress * this.totalFrames), this.totalFrames - 1);
      if (newTarget !== this.targetFrame) {
        this.targetFrame = newTarget;
        if (!this._isAnimating) {
          this._isAnimating = true;
          this.animateLoop();
        }
      }
    };

    window.addEventListener('scroll', this._handleScroll, { passive: true });
    this._handleScroll(); // 初始化执行一次，防止首屏空白

    // 启动负责“丝滑过渡”的渲染主循环
    this.animateLoop();
  }

  animateLoop() {
    // 核心 Lerp 公式：当前值 += (目标值 - 当前值) * 缓动系数 (0.1)
    this.interpolatedFrame += (this.targetFrame - this.interpolatedFrame) * 0.1;
    const roundedFrame = Math.round(this.interpolatedFrame);

    if (roundedFrame !== this.currentFrame) {
      this.currentFrame = roundedFrame;
      this.drawCanvas(roundedFrame);
      this.updateTextBlocks(roundedFrame);
    }

    // Lerp 收敛后停止循环，等 scroll 事件再次唤醒
    if (Math.abs(this.interpolatedFrame - this.targetFrame) > 0.05) {
      this._rafId = requestAnimationFrame(() => this.animateLoop());
    } else {
      this._isAnimating = false;
    }
  }

  // 缓动函数（用于文字进出场动画）
  ease(t) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; }

  // 1. 修改后的 getTransform：引入动态缩放，让 CSS 中的 transform-origin 活过来
  getTransform(pos, ty, opacity) {
    // 随着透明度从 0 变到 1，缩放比例从 0.92 丝滑放大到 1
    const scale = 0.92 + (0.08 * opacity); 
    
    const transformExtra = `scale(${scale})`;

    // 使用独立的 translateY(-50%) + translateY(ty) 代替 calc()，兼容性更好
    if (pos === "left" || pos === "right" || pos === "center") {
      return `translateY(-50%) translateY(${ty}px) ${transformExtra}`;
    }
    return `translateY(${ty}px) ${transformExtra}`; 
  }

  // 2. 修改后的 updateTextBlocks：在调用时把 opacity 传过去
  updateTextBlocks(f) {
    this.config.blocks.forEach(b => {
      const el = document.getElementById(b.id);
      if (!el) return;
      
      let opacity = 0, ty = 0;
      
      if (f <= b.from) {
        opacity = 0; ty = -28;
      } else if (f < b.from + this.fadeRange) {
        const t = (f - b.from) / this.fadeRange;
        opacity = this.ease(t); ty = -28 * (1 - this.ease(t));
      } else if (f <= b.to - this.fadeRange) {
        opacity = 1; ty = 0;
      } else if (f < b.to) {
        const t = (f - (b.to - this.fadeRange)) / this.fadeRange;
        opacity = 1 - this.ease(t); ty = 28 * this.ease(t);
      } else {
        opacity = 0; ty = 28;
      }

      el.style.opacity = opacity;
      el.style.transform = this.getTransform(b.pos, ty, opacity);
      // 仅在动画活跃帧范围内开启 GPU 合成层，其余时间释放
      el.style.willChange = (f > b.from && f < b.to) ? 'opacity, transform' : 'auto';
    });
  }

  dispose() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    if (this._handleScroll) {
      window.removeEventListener('scroll', this._handleScroll);
      this._handleScroll = null;
    }
    clearTimeout(this._resizeTimer);
    // 释放图片内存
    this.images = null;
    this.ctx = null;
    this.canvas = null;
  }
}

/* ==========================================
   🚀 全生命周期初始化安全加载器（含 Shopify 后台适配）
   ========================================== */
const scrollSequenceInstances = new Map();

function initAllScrollSequences() {
  const sections = document.querySelectorAll('.rv-scroll-section');
  sections.forEach(sec => {
    if (!sec.classList.contains('js-sequence-initialized')) {
      sec.classList.add('js-sequence-initialized');
      scrollSequenceInstances.set(sec, new ScrollSequence(sec));
    }
  });
}

// 正常页面加载逻辑
if (document.readyState === 'loading') {
  document.addEventListener("DOMContentLoaded", initAllScrollSequences);
} else {
  initAllScrollSequences();
}

// 适配 Shopify Theme Editor 后台：确保商家点击“保存”或者调整参数时，动态重载动画
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.rv-scroll-section')) {
    initAllScrollSequences();
  }
});

// 适配 Shopify Theme Editor 卸载：清理监听器和内存
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('.rv-scroll-section').forEach(sec => {
    const instance = scrollSequenceInstances.get(sec);
    if (instance) {
      instance.dispose();
      scrollSequenceInstances.delete(sec);
    }
    sec.classList.remove('js-sequence-initialized');
  });
});